import { useCallback, useEffect, useRef, useState } from 'react'

interface UseCarouselDotsOptions {
  itemCount: number
  itemWidth: number
}

/**
 * Tracks which carousel item is in view and provides scroll-to-index.
 * Attach the returned ref to the scroll container; child items must have
 * data-carousel-post and data-carousel-index attributes.
 */
export const useCarouselDots = ({ itemCount, itemWidth }: UseCarouselDotsOptions) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)

  const updateActiveIndexFromScroll = useCallback(() => {
    const container = containerRef.current
    if (!container || itemCount === 0) return

    const items = container.querySelectorAll('[data-carousel-post]')
    if (items.length === 0) return

    const containerRect = container.getBoundingClientRect()
    const containerCenter = containerRect.left + containerRect.width / 2

    let closestIndex = 0
    let minDistance = Infinity

    items.forEach((el, i) => {
      const rect = el.getBoundingClientRect()
      const elCenter = rect.left + rect.width / 2
      const distance = Math.abs(containerCenter - elCenter)
      if (distance < minDistance) {
        minDistance = distance
        closestIndex = i
      }
    })

    setActiveIndex(closestIndex)
  }, [itemCount])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    updateActiveIndexFromScroll()
    container.addEventListener('scroll', updateActiveIndexFromScroll)
    return () => container.removeEventListener('scroll', updateActiveIndexFromScroll)
  }, [updateActiveIndexFromScroll])

  const scrollToIndex = useCallback(
    (index: number) => {
      const container = containerRef.current
      if (!container) return
      const scrollPosition = index * itemWidth
      container.scrollTo({ left: scrollPosition, behavior: 'smooth' })
    },
    [itemWidth],
  )

  return {
    containerRef,
    activeIndex,
    scrollToIndex,
  }
}
